import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import { DynamicPage, DynamicPageTag } from '@superfunnel/types';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Button from '@superfunnel/ui-core/v2/Button';
import { Calendar } from '@superfunnel/ui-core/v2/Calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@superfunnel/ui-core/v2/popover';
import {
  PopoverPanelActions,
  PopoverPanelBody,
  PopoverPanelContent,
  PopoverPanelFooter,
  PopoverPanelHeader,
  PopoverPanelTitle,
} from '@superfunnel/ui-core/v2/PopoverPanel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import Switch from '@superfunnel/ui-core/v2/Switch';
import { format, subDays } from 'date-fns';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { DashboardContext } from '../contexts/DashboardContext';
import { useExportLeadsContext } from '../contexts/ExportLeadsContext';
import { UserContext } from '../contexts/UserContext';
import { ExportFormat, LeadType } from '../services/LeadsService';

function classNames(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

interface Props {
  children: ReactNode;
  disabled?: boolean;
  /** Pre-selected dynamic page IDs (e.g., when opened from a specific dynamic page) */
  defaultDynamicPageIds?: string[];
  /** Pre-selected lead type */
  defaultLeadType?: LeadType;
}

export interface DateRange {
  from: Date;
  to: Date;
}

export default function ExportLeadsPopover({
  children,
  disabled,
  defaultDynamicPageIds,
  defaultLeadType = 'leads',
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages: campaigns } = useContext(DashboardContext);
  const { exportLeads } = useExportLeadsContext();

  const [open, setOpen] = useState(false);

  const campaignTags = useMemo(() => {
    const tagsMap = new Map<string, DynamicPageTag>();
    campaigns?.forEach((c) =>
      c.data().tags?.forEach((t) => tagsMap.set(t.id, t)),
    );

    return Array.from(tagsMap.values());
  }, [campaigns]);

  const [dateRangeValue, setDateRangeValue] = useState('7D');
  const [customDateRange, setCustomDateRange] = useState<DateRange>();
  const [tempRange, setTempRange] = useState<{ from?: Date; to?: Date }>({});
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDynamicPages, setSelectedDynamicPages] =
    useState<QueryDocumentSnapshot<DynamicPage, DynamicPage>[]>();
  const [selectedDynamicPageTags, setSelectedDynamicPageTags] = useState<
    DynamicPageTag[]
  >([]);
  const [leadType, setLeadType] = useState<LeadType>(defaultLeadType);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error>();

  // Reset state when popover opens
  useEffect(() => {
    if (open) {
      setDateRangeValue('7D');
      setCustomDateRange(undefined);
      setLeadType(defaultLeadType);
      setExportFormat('csv');
      setCreating(false);
      setError(undefined);

      // Set default selected dynamic pages if provided
      if (defaultDynamicPageIds && campaigns) {
        const defaultPages = campaigns.filter((c) =>
          defaultDynamicPageIds.includes(c.id),
        );
        if (defaultPages.length > 0) {
          setSelectedDynamicPages(defaultPages);
        } else {
          setSelectedDynamicPages(undefined);
        }
      } else {
        setSelectedDynamicPages(undefined);
      }
      setSelectedDynamicPageTags([]);
    }
  }, [open, defaultDynamicPageIds, defaultLeadType, campaigns]);

  // Calculate date range based on selection
  const getDateRange = useCallback(() => {
    if (customDateRange) {
      return {
        startDate: format(customDateRange.from, 'yyyy-MM-dd'),
        endDate: format(customDateRange.to, 'yyyy-MM-dd'),
      };
    }

    const today = new Date();
    let startDate: Date;

    switch (dateRangeValue) {
      case '1D':
        startDate = today;
        break;
      case '7D':
        startDate = subDays(today, 6);
        break;
      case '1M':
        startDate = subDays(today, 29);
        break;
      case '3M':
        startDate = subDays(today, 89);
        break;
      default:
        startDate = subDays(today, 6);
    }

    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(today, 'yyyy-MM-dd'),
    };
  }, [customDateRange, dateRangeValue]);

  // Date range helper functions
  const formatDateRangeDisplay = (range: DateRange) => {
    const formatDate = (date: Date) => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      }
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    };

    if (range.from.toDateString() === range.to.toDateString()) {
      return formatDate(range.from);
    }

    return `${formatDate(range.from)} - ${formatDate(range.to)}`;
  };

  const handleApplyRange = () => {
    if (tempRange.from && tempRange.to) {
      setCustomDateRange({
        from: tempRange.from,
        to: tempRange.to,
      });
      setCalendarOpen(false);
      setDateRangeValue('custom');
    }
  };

  const handleDateRangeChange = (newValue: string) => {
    if (newValue && newValue !== 'custom' && customDateRange) {
      setCustomDateRange(undefined);
    }
    setDateRangeValue(newValue);
  };

  const getCustomLabel = () => {
    if (customDateRange) {
      return formatDateRangeDisplay(customDateRange);
    }
    return (
      <span className="inline-flex items-center gap-1">
        <CalendarIcon className="size-4" />
        Custom
      </span>
    );
  };

  // Initialize tempRange with current customDateRange when calendar opens
  useEffect(() => {
    if (calendarOpen && customDateRange) {
      setTempRange({
        from: customDateRange.from,
        to: customDateRange.to,
      });
    } else if (!calendarOpen) {
      setTempRange({});
    }
  }, [calendarOpen, customDateRange]);

  const onExportClick = useCallback(async () => {
    if (!selectedAccount) return;

    setCreating(true);
    setError(undefined);

    try {
      const { startDate, endDate } = getDateRange();

      // Collect dynamic page IDs from selected pages and tags
      const campaignTagIdsSet = new Set(
        selectedDynamicPageTags.map((t) => t.id),
      );
      const campaignIdsFromTags =
        campaigns
          ?.filter((c) =>
            c.data().tags?.some((t) => campaignTagIdsSet.has(t.id)),
          )
          .map((c) => c.id) ?? [];

      const dynamicPageIds = selectedDynamicPages
        ? Array.from(
            new Set([
              ...selectedDynamicPages.map((c) => c.id),
              ...campaignIdsFromTags,
            ]),
          )
        : undefined;

      await exportLeads({
        accountId: selectedAccount.id,
        startDate,
        endDate,
        type: leadType,
        dynamicPageIds,
        format: exportFormat,
      });

      setOpen(false);
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    } finally {
      setCreating(false);
    }
  }, [
    selectedAccount,
    getDateRange,
    selectedDynamicPageTags,
    campaigns,
    selectedDynamicPages,
    exportLeads,
    leadType,
    exportFormat,
  ]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverPanelContent width="md" align="end">
        <PopoverPanelHeader>
          <PopoverPanelTitle>Export Leads</PopoverPanelTitle>
        </PopoverPanelHeader>
        <PopoverPanelBody className="flex flex-col gap-4">
          {/* Lead Type */}
          <div className="space-y-2">
            <h3 className="text-base-700 text-sm font-medium">Lead Type</h3>
            <Select
              value={leadType}
              onValueChange={(v) => setLeadType(v as LeadType)}
              disabled={creating}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select lead type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="leads">Leads</SelectItem>
                <SelectItem value="partialLeads">Partial Leads</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <h3 className="text-base-700 text-sm font-medium">Date range</h3>
            <ToggleGroup.Root
              type="single"
              value={customDateRange ? 'custom' : dateRangeValue}
              onValueChange={(v) => v && handleDateRangeChange(v)}
              className="bg-base-100 flex w-full items-center gap-0.5 rounded-lg p-0.5 text-sm"
            >
              <ToggleGroup.Item
                value="1D"
                className={classNames(
                  'text-base-500 h-[28px] flex-1 rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none',
                  'hover:text-foreground',
                  'data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white',
                  'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2',
                )}
              >
                1D
              </ToggleGroup.Item>
              <ToggleGroup.Item
                value="7D"
                className={classNames(
                  'text-base-500 h-[28px] flex-1 rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none',
                  'hover:text-foreground',
                  'data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white',
                  'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2',
                )}
              >
                7D
              </ToggleGroup.Item>
              <ToggleGroup.Item
                value="1M"
                className={classNames(
                  'text-base-500 h-[28px] flex-1 rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none',
                  'hover:text-foreground',
                  'data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white',
                  'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2',
                )}
              >
                1M
              </ToggleGroup.Item>
              <ToggleGroup.Item
                value="3M"
                className={classNames(
                  'text-base-500 h-[28px] flex-1 rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none',
                  'hover:text-foreground',
                  'data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white',
                  'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2',
                )}
              >
                3M
              </ToggleGroup.Item>
              <div className="bg-base-300 mx-1 h-4 w-px self-center" />
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <ToggleGroup.Item
                    value="custom"
                    data-state={customDateRange ? 'on' : 'off'}
                    className={classNames(
                      'text-base-500 h-[28px] flex-1 rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none',
                      'hover:text-foreground',
                      customDateRange
                        ? 'text-base-900 shadow-tabs rounded-lg bg-white'
                        : 'data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white',
                      'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2',
                    )}
                  >
                    {getCustomLabel()}
                  </ToggleGroup.Item>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto rounded-2xl bg-white p-4 px-3"
                  align="start"
                >
                  <Calendar
                    mode="range"
                    selected={
                      tempRange.from && tempRange.to
                        ? { from: tempRange.from, to: tempRange.to }
                        : customDateRange
                          ? {
                              from: customDateRange.from,
                              to: customDateRange.to,
                            }
                          : undefined
                    }
                    onSelect={(range: any) => {
                      setTempRange(range || {});
                    }}
                    numberOfMonths={1}
                  />
                  <div className="border-base-200 mt-3 border-t py-3 pb-0">
                    <div className="flex items-center justify-between">
                      <span className="text-base-500 text-sm font-normal tracking-[0.015em]">
                        {tempRange.from && tempRange.to ? (
                          <span className="text-base-500">
                            Selected:
                            <span className="text-base-900">
                              {' '}
                              {`${Math.ceil(Math.abs(tempRange.to.getTime() - tempRange.from.getTime()) / (1000 * 60 * 60 * 24)) + 1} days`}
                            </span>
                          </span>
                        ) : (
                          'Select dates'
                        )}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleApplyRange}
                          disabled={!tempRange.from || !tempRange.to}
                          className="h-8 rounded-lg border-0 bg-[#795DFC] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#6B4FE8]"
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </ToggleGroup.Root>
          </div>

          {/* Export Format */}
          <div className="space-y-2">
            <h3 className="text-base-700 text-sm font-medium">Export Format</h3>
            <Select
              value={exportFormat}
              onValueChange={(v) => setExportFormat(v as ExportFormat)}
              disabled={creating}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Dynamic Pages Filter */}
          <div className="flex items-center justify-start gap-2">
            <Switch
              size="sm"
              checked={!selectedDynamicPages}
              disabled={creating}
              onCheckedChange={(checked: boolean) => {
                setSelectedDynamicPages(checked ? undefined : []);
              }}
            />
            <label className="text-base-700 text-sm font-medium">
              Export All Dynamic Pages
            </label>
          </div>
          {selectedDynamicPages && (
            <>
              <ChipsAutocomplete<
                QueryDocumentSnapshot<DynamicPage, DynamicPage>
              >
                title="Dynamic Pages"
                options={campaigns || []}
                getOptionId={(c) => c.id}
                getValue={(c) => c.data().name}
                selected={selectedDynamicPages || []}
                onChange={setSelectedDynamicPages}
                className="w-full"
                disabled={creating || !selectedDynamicPages}
              />
              <ChipsAutocomplete<DynamicPageTag>
                title="Tags"
                options={campaignTags || []}
                getOptionId={(c) => c.id}
                getValue={(c) => c.name}
                selected={selectedDynamicPageTags}
                onChange={setSelectedDynamicPageTags}
                className="w-full"
                disabled={creating || !selectedDynamicPages}
              />
            </>
          )}
        </PopoverPanelBody>

        <PopoverPanelFooter>
          <PopoverPanelActions
            actions={[
              {
                type: 'secondary',
                title: 'Cancel',
                disabled: creating,
                onClick: () => setOpen(false),
              },
              {
                type: 'primary',
                title: creating ? 'Exporting...' : 'Export',
                disabled: creating,
                loading: creating,
                onClick: onExportClick,
              },
            ]}
          />
        </PopoverPanelFooter>

        {error && (
          <div className="mt-5 w-full text-end text-red-600">
            {error.message || 'Failed to export leads'}
          </div>
        )}
      </PopoverPanelContent>
    </Popover>
  );
}
